import React, { ReactNode } from 'react';
import BlankLayout from '../components/layouts/Blank';
import MainLayout from '../components/layouts/Layout';
import { RoutePath, ROUTES } from '../constants/routes';

export interface RouteConfig {
  path: RoutePath;
  component: React.ComponentType;
  layout?: React.ComponentType<{ children: ReactNode; hideAppbar?: boolean }>;
  children?: RouteConfig[];
  protected?: boolean;
  hideAppbar?: boolean;
  title?: string;
}

// Route configuration
export const routeConfig: RouteConfig[] = [
  {
    path: ROUTES.HOME,
    component: React.lazy(() => import('../pages/HomePage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
  },
  {
    path: ROUTES.LOGIN,
    component: React.lazy(() => import('../pages/auth/LoginPage').then(m => ({ default: m.default }))),
  },
  {
    path: ROUTES.SIGN_UP,
    component: React.lazy(() => import('../pages/user/SignUpPage').then(m => ({ default: m.default }))),
    hideAppbar: true,
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    component: React.lazy(() => import('../pages/forgot-password/ForgotPasswordPage').then(m => ({ default: m.default }))),
  },
  {
    path: ROUTES.DASHBOARD,
    component: React.lazy(() => import('../pages/DashboardPage').then(m => ({ default: m.default }))),
    layout: MainLayout,
    protected: true,
  },
  {
    path: ROUTES.ASSETS,
    component: React.lazy(() => import('../pages/assets').then(m => ({ default: m.default }))),
    layout: MainLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.OVERVIEW,
    component: React.lazy(() => import('../pages/overview').then(m => ({ default: m.default }))),
    layout: MainLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.BUDGET,
    component: React.lazy(() => import('../pages/budget').then(m => ({ default: m.default }))),
    layout: MainLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.CREATE_BASIC_WALLET,
    component: React.lazy(() => import('../pages/basic-wallet/BasicWalletPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.EDIT_BASIC_WALLET,
    component: React.lazy(() => import('../pages/wallets/[id]/basic/edit').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.BORROWING_TRANSACTION_EDIT,
    component: React.lazy(() => import('../components/features/EditLoanTransaction/EditBorrowingTransaction').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.REPAYMENT_TRANSACTION_EDIT,
    component: React.lazy(() => import('../components/features/EditLoanTransaction/EditRepaymentTransaction').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.COLLECTING_TRANSACTION_EDIT,
    component: React.lazy(() => import('../components/features/EditLoanTransaction/EditCollectingTransaction').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.CLEAR_DEBT_TRANSACTION_EDIT,
    component: React.lazy(() => import('../components/features/EditLoanTransaction/EditClearDebtTransaction').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.LENDING_TRANSACTION_EDIT,
    component: React.lazy(() => import('../components/features/EditLoanTransaction/EditLendingLoanTransaction').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.CREATE_CREDIT_WALLET,
    component: React.lazy(() => import('../pages/wallets/create/credit-wallet/CreateCreditWalletPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.EDIT_LOAN_TRANSACTION,
    component: React.lazy(() => import('../components/features/EditLoanTransaction').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.EDIT_LOAN_TRANSACTION,
    component: React.lazy(() => import('../components/features/EditLoanTransaction').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.CREATE_DEBT_WALLET,
    component: React.lazy(() => import('../pages/wallets/create/debt-wallet/CreateDebtWalletPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.CREATE_LOAN_WALLET,
    component: React.lazy(() => import('../pages/wallets/create/loan-wallet/CreateLoanWalletPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.EDIT_LOAN_WALLET,
    component: React.lazy(() => import('../pages/wallets/[id]/loan/EditLoanWalletPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.HISTORY,
    component: React.lazy(() => import('../pages/transactions-history/TransactionHistoryPage').then(m => ({ default: m.default }))),
    layout: MainLayout,
    protected: true,
    hideAppbar: true,
    children: [
      {
        path: ROUTES.TRANSACTION_DETAIL,
        component: React.lazy(() => import('../pages/transactions/[id]/transaction-details').then(m => ({ default: m.default }))),
      },
      {
        path: ROUTES.BORROWING_TRANSACTION_DETAIL,
        component: React.lazy(() => import('../pages/transactions/borrowing/[id]/transaction-details').then(m => ({ default: m.default }))),
      },
      {
        path: ROUTES.REPAYMENT_TRANSACTION_DETAIL,
        component: React.lazy(() => import('../pages/transactions/repayment/[id]/transaction-details').then(m => ({ default: m.default }))),
      },
      {
        path: ROUTES.LENDING_TRANSACTION_DETAIL,
        component: React.lazy(() => import('../pages/transactions/lending/[id]/transaction-details').then(m => ({ default: m.default }))),
      },
      {
        path: ROUTES.COLLECTING_TRANSACTION_DETAIL,
        component: React.lazy(() => import('../pages/transactions/collecting/[id]/transaction-details').then(m => ({ default: m.default }))),
      },
      {
        path: ROUTES.CLEAR_DEBT_TRANSACTION_DETAIL,
        component: React.lazy(() => import('../pages/transactions/clear-debt/[id]/transaction-details').then(m => ({ default: m.default }))),
      }
    ],
  },
  {
    path: ROUTES.CREATE_BASIC_TRANSACTION,
    component: React.lazy(() => import('../pages/transactions/create/basic-transactions/BasicTransactionsPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
  },
  {
    path: ROUTES.EDIT_BASIC_TRANSACTION,
    component: React.lazy(() => import('../pages/transactions/create/basic-transactions/BasicTransactionsPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
  },
  {
    path: ROUTES.CREDIT_WALLET_DETAIL,
    component: React.lazy(() => import('../pages/wallets/[id]/statements/credit').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.BASIC_WALLET_DETAIL,
    component: React.lazy(() => import('../pages/wallets/[id]/basic/BasicWalletDetails').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.LOAN_WALLET_DETAIL,
    component: React.lazy(() => import('../pages/wallets/[id]/loan/LoanWalletDetails').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.LOAN_WALLET_OFFSET,
    component: React.lazy(() => import('../pages/wallets/[id]/loan/LoanOffsetPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
  },
  {
    path: ROUTES.EDIT_WALLET,
    component: React.lazy(() => import('../pages/wallets/[id]/edit').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.WALLET_INSTALLMENT,
    component: React.lazy(() => import('../pages/wallets/[id]/installment').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.CREDIT_STATEMENT_DETAIL,
    component: React.lazy(() => import('../pages/wallets/[id]/statements/[id]/StatementDetails').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    hideAppbar: true,
    protected: true,
    children: [
      {
        path: ROUTES.CREDIT_TRANSACTION_DETAIL,
        component: React.lazy(() => import('../pages/transactions/[id]/transaction-details').then(m => ({ default: m.default }))),
      },
    ],
  },
  {
    path: ROUTES.PROFILE,
    component: React.lazy(() => import('../pages/user/ProfilePage').then(m => ({ default: m.default }))),
    layout: MainLayout,
    protected: true,
  },
  {
    path: ROUTES.ICONS,
    component: React.lazy(() => import('../pages/IconsPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
  },
  {
    path: ROUTES.MINO_TERMS,
    component: React.lazy(() => import('../pages/mimo-terms/MimoTermsPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
  },
  {
    path: ROUTES.DISCOVER,
    component: React.lazy(() => import('../pages/discover').then(m => ({ default: m.default }))),
    layout: MainLayout,
    protected: true,
    hideAppbar: true,
  },
  {
    path: ROUTES.SETTINGS,
    component: React.lazy(() => import('../pages/user/SettingsPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
  },
  {
    path: ROUTES.UPGRADE_ACCOUNT,
    component: React.lazy(() => import('../pages/upgrade').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
  },
  {
    path: ROUTES.CATEGORIES,
    component: React.lazy(() => import('../pages/categories/CreateCategoryPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
  },
  {
    path: ROUTES.CATEGORY_GROUP,
    component: React.lazy(() => import('../pages/categories/CreateCategoryGroupPage').then(m => ({ default: m.default }))),
    layout: BlankLayout,
    protected: true,
  },
];